import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { getCityVariant, type CityVariant } from "@/lib/cityVariant";
import { measureOpenAI } from "@/lib/openaiPixel";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    fbq?: (...args: unknown[]) => void;
  }
}

/**
 * Push a GA4 + GTM event. Safe on SSR (no-ops if window/gtag missing).
 * Mirrors to dataLayer so GTM triggers can also fire on the same event.
 */
function trackEvent(name: string, params: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  try {
    window.gtag?.("event", name, params);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...params });
  } catch {
    /* swallow analytics errors */
  }
}

export const REFERRAL_SOURCES = [
  "Search engine",
  "ChatGPT",
  "Claude",
  "Gemini",
  "AI",
  "Friend/referral",
  "TV",
  "Other",
] as const;

const ContactSchema = z.object({
  firstName: z.string().trim().min(1, "Please enter your first name").max(80),
  lastName: z.string().trim().min(1, "Please enter your last name").max(80),
  phone: z
    .string()
    .trim()
    .min(7, "Please enter a valid phone")
    .max(30)
    .regex(/^[0-9+()\-.\s]+$/, "Numbers and ()+-. only"),
  email: z.string().trim().email("Please enter a valid email").max(255),
  businessName: z.string().trim().min(1, "Please enter your business name").max(150),
  websiteLink: z
    .string()
    .trim()
    .max(255)
    .optional()
    .or(z.literal(""))
    .refine(
      (v) => !v || /^(https?:\/\/)?[^\s]+\.[^\s]+$/.test(v),
      "Please enter a valid website URL",
    ),
  message: z.string().trim().min(5, "Please tell us a bit more").max(2000),
  referralSource: z.enum(REFERRAL_SOURCES).optional().or(z.literal("")),
  consent: z.literal(true, {
    errorMap: () => ({ message: "Please agree to be contacted to continue" }),
  }),
});

type FormState = {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  businessName: string;
  websiteLink: string;
  message: string;
  referralSource: "" | (typeof REFERRAL_SOURCES)[number];
  consent: boolean;
};
type FieldErrors = Partial<Record<keyof FormState, string>>;

const initial: FormState = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  businessName: "",
  websiteLink: "",
  message: "",
  referralSource: "",
  consent: false,
};

export function ContactForm() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as {
    message?: string;
    email?: string;
    topic?: string;
    quote?: string;
  };
  const [values, setValues] = useState<FormState>(() => ({
    ...initial,
    message: search.message ?? "",
    email: search.email ?? "",
  }));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [website, setWebsite] = useState(""); // honeypot #1
  const [companyUrl, setCompanyUrl] = useState(""); // honeypot #2
  const formLoadedAtRef = useRef<number>(Date.now());
  const submittedRef = useRef(false);
  const sessionIdRef = useRef<string>("");
  const lastFieldRef = useRef<string>("");
  const startTrackedRef = useRef(false);
  const cityVariantRef = useRef<CityVariant | null>(null);
  const referringPageRef = useRef<string | null>(null);

  // Capture the page the visitor was on before opening the contact form.
  // Set by the root route on every non-/contact navigation.
  useEffect(() => {
    try {
      referringPageRef.current = sessionStorage.getItem("cwi_referring_page");
    } catch {
      referringPageRef.current = null;
    }
  }, []);

  // Detect lead intent from the prefilled message (audit, proposal, etc.).
  // Used as the `lead_type` param on every GA4 event for this submission.
  const leadType = (() => {
    const m = (search.message ?? "").toLowerCase();
    if (/audit|review my (site|website)/.test(m)) return "audit";
    if (/proposal|quote|estimate/.test(m)) return "proposal";
    if (/seo/.test(m)) return "seo";
    if (/web ?design|website/.test(m)) return "web_design";
    return "contact";
  })();

  // Initialize / restore a session id for partial-save tracking
  // and resolve the sticky city callout variant for this visitor.
  useEffect(() => {
    try {
      const KEY = "cwi_contact_session_id";
      let id = sessionStorage.getItem(KEY);
      if (!id) {
        id =
          (typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : Math.random().toString(36).slice(2) + Date.now().toString(36));
        sessionStorage.setItem(KEY, id);
      }
      sessionIdRef.current = id;
    } catch {
      sessionIdRef.current = Math.random().toString(36).slice(2) + Date.now().toString(36);
    }
    cityVariantRef.current = getCityVariant();
  }, []);

  // Debounced partial save when the user types
  useEffect(() => {
    if (submittedRef.current) return;
    if (!sessionIdRef.current) return;
    const handle = setTimeout(() => {
      const hasContent =
        values.firstName || values.lastName || values.email ||
        values.phone || values.businessName || values.message;
      if (!hasContent) return;
      fetch("/api/contact-partial", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: sessionIdRef.current,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone,
          businessName: values.businessName,
          websiteLink: values.websiteLink,
          message: values.message,
          referralSource: values.referralSource || "",
          lastField: lastFieldRef.current,
          cityVariant: cityVariantRef.current,
          formLoadedAt: formLoadedAtRef.current,
          companyUrl,
        }),
        keepalive: true,
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(handle);
  }, [values]);

  const update = <K extends keyof FormState>(key: K, val: FormState[K]) => {
    lastFieldRef.current = String(key);
    // Fire `form_start` once, the first time a user actually edits a field.
    // GA4 standard recommended event for measuring form engagement funnel.
    if (!startTrackedRef.current) {
      startTrackedRef.current = true;
      trackEvent("form_start", {
        form_id: "contact",
        form_name: "Contact Form",
        lead_type: leadType,
        city_variant: cityVariantRef.current,
      });
    }
    if (submitError) setSubmitError(null);
    setValues((v) => ({ ...v, [key]: val }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const cityVariant = cityVariantRef.current;
    const parsed = ContactSchema.safeParse(values);
    if (!parsed.success) {
      const fieldErrors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const k = issue.path[0] as keyof FormState;
        if (k && !fieldErrors[k]) fieldErrors[k] = issue.message;
      }
      setErrors(fieldErrors);
      toast.error("Please fix the highlighted fields.");
      // Track validation failures so we can see which forms get stuck.
      trackEvent("form_validation_error", {
        form_id: "contact",
        lead_type: leadType,
        city_variant: cityVariant,
        error_fields: Object.keys(fieldErrors).join(","),
      });
      return;
    }

    // Fire on every submit attempt (post-validation, pre-network).
    trackEvent("form_submit", {
      form_id: "contact",
      form_name: "Contact Form",
      lead_type: leadType,
      city_variant: cityVariant,
    });

    setSubmitting(true);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...parsed.data,
          website,
          companyUrl,
          formLoadedAt: formLoadedAtRef.current,
          cityVariant,
          topic: search.topic ?? "",
          quote: search.quote ?? "",
          referringPage: referringPageRef.current ?? "",
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        const msg = data.error || "Something went wrong. Please try again.";
        setSubmitError(msg);
        toast.error(msg);
        trackEvent("form_submit_error", {
          form_id: "contact",
          lead_type: leadType,
          city_variant: cityVariant,
          status: res.status,
        });
        return;
      }
      toast.success("Thanks! We'll be in touch within 24 hours.");
      submittedRef.current = true;
      try { sessionStorage.removeItem("cwi_contact_session_id"); } catch {}
      // GA4 recommended event for lead capture. `value` + `currency` make
      // the event eligible to be marked a Key Event in GA4 with a value.
      trackEvent("generate_lead", {
        form_id: "contact",
        form_name: "Contact Form",
        lead_type: leadType,
        city_variant: cityVariant,
        method: "contact_form",
        currency: "USD",
        value: 1,
      });
      // Meta Pixel — Lead event for Facebook/Instagram Ads optimization.
      window.fbq?.("track", "Lead", {
        content_name: leadType === "audit" ? "Free Audit Request" : "Contact Form",
        content_category: leadType,
        city_variant: cityVariant,
        currency: "USD",
        value: 1,
      });
      // OpenAI Ads — lead_created conversion event.
      measureOpenAI(
        "lead_created",
        { type: "customer_action", lead_type: leadType, city_variant: cityVariant },
        { event_id: sessionIdRef.current, value: 1, currency: "USD" },
      );
      try { sessionStorage.setItem("cwi_openai_lead_fired", "1"); } catch {}
      // Audit-specific event so you can build a separate audit funnel report.
      if (leadType === "audit") {
        trackEvent("audit_request", {
          form_id: "contact",
          method: "contact_form",
          city_variant: cityVariant,
          currency: "USD",
          value: 1,
        });
      }
      setValues(initial);
      navigate({ to: "/thank-you", search: { source: leadType === "audit" ? "audit" : "contact" } });
    } catch {
      const msg = "Network error. Please try again or call (719) 419-3935.";
      setSubmitError(msg);
      toast.error(msg);
      trackEvent("form_submit_error", {
        form_id: "contact",
        lead_type: leadType,
        city_variant: cityVariant,
        status: "network_error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5">
      {/* Honeypots — hidden from real users; only bots fill these */}
      <div aria-hidden="true" className="absolute left-[-10000px] h-0 w-0 overflow-hidden">
        <label>
          Website
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </label>
        <label>
          Company URL
          <input
            type="text"
            name="company_url"
            tabIndex={-1}
            autoComplete="off"
            value={companyUrl}
            onChange={(e) => setCompanyUrl(e.target.value)}
          />
        </label>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="firstName" label="First name" error={errors.firstName} required>
          <Input
            id="firstName"
            autoComplete="given-name"
            required
            value={values.firstName}
            onChange={(e) => update("firstName", e.target.value)}
            aria-invalid={!!errors.firstName}
          />
        </Field>
        <Field id="lastName" label="Last name" error={errors.lastName} required>
          <Input
            id="lastName"
            autoComplete="family-name"
            required
            value={values.lastName}
            onChange={(e) => update("lastName", e.target.value)}
            aria-invalid={!!errors.lastName}
          />
        </Field>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="phone" label="Phone" error={errors.phone} required>
          <Input
            id="phone"
            type="tel"
            autoComplete="tel"
            required
            value={values.phone}
            onChange={(e) => update("phone", e.target.value)}
            aria-invalid={!!errors.phone}
          />
        </Field>
        <Field id="email" label="Email" error={errors.email} required>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={values.email}
            onChange={(e) => update("email", e.target.value)}
            aria-invalid={!!errors.email}
          />
        </Field>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="businessName" label="Business name" error={errors.businessName} required>
          <Input
            id="businessName"
            autoComplete="organization"
            value={values.businessName}
            onChange={(e) => update("businessName", e.target.value)}
            aria-invalid={!!errors.businessName}
          />
        </Field>
        <Field id="websiteLink" label="Website link" error={errors.websiteLink}>
          <Input
            id="websiteLink"
            type="url"
            autoComplete="url"
            value={values.websiteLink}
            onChange={(e) => update("websiteLink", e.target.value)}
            aria-invalid={!!errors.websiteLink}
          />
        </Field>
      </div>
      <Field id="message" label="How can we help you succeed online?" error={errors.message} required>
        <Textarea
          id="message"
          rows={5}
          value={values.message}
          onChange={(e) => update("message", e.target.value)}
          aria-invalid={!!errors.message}
        />
      </Field>

      <Field id="referralSource" label="How did you hear about us?" error={errors.referralSource}>
        <select
          id="referralSource"
          value={values.referralSource}
          onChange={(e) =>
            update(
              "referralSource",
              e.target.value as FormState["referralSource"],
            )
          }
          aria-invalid={!!errors.referralSource}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <option value="">Select an option (optional)</option>
          {REFERRAL_SOURCES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </Field>


      <div className="space-y-2">
        <div className="flex items-start gap-3 rounded-md border border-border bg-muted/30 p-4">
          <Checkbox
            id="consent"
            checked={values.consent}
            onCheckedChange={(checked) => update("consent", checked === true)}
            aria-invalid={!!errors.consent}
            className="mt-0.5"
          />
          <Label htmlFor="consent" className="text-xs font-normal leading-relaxed text-muted-foreground">
            I agree to receive emails, phone calls, and SMS messages from Colorado Web Impressions
            regarding my inquiry. Message frequency varies. Message and data rates may apply. Reply
            STOP to unsubscribe, HELP for help. We never sell or share your phone number.{" "}
            <Link to="/privacy" className="underline hover:text-foreground">
              how we handle your data
            </Link>{" "}
            ·{" "}
            <Link to="/sms-policy" className="underline hover:text-foreground">
              SMS Policy
            </Link>{" "}
            ·{" "}
            <Link to="/terms" className="underline hover:text-foreground">
              Terms of Service
            </Link>
          </Label>
        </div>
        {errors.consent ? (
          <p className="text-xs font-medium text-destructive" role="alert">
            {errors.consent}
          </p>
        ) : null}
      </div>

      {submitError ? (
        <div
          role="alert"
          aria-live="assertive"
          className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
        >
          {submitError}
        </div>
      ) : null}

      <Button
        type="submit"
        size="lg"
        disabled={submitting}
        className="w-full bg-gradient-gold font-bold uppercase tracking-wider text-secondary shadow-elegant transition-all hover:opacity-90 hover:shadow-lg sm:w-auto"
      >
        {submitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Sending...
          </>
        ) : (
          "Send message"
        )}
      </Button>
    </form>
  );
}

function Field({
  id,
  label,
  error,
  required,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-semibold">
        {label}
        {required ? <span className="ml-0.5 text-destructive">*</span> : null}
      </Label>
      {children}
      {error ? (
        <p className="text-xs font-medium text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
